import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { UserContext } from "../contexts/UserContext";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isoToDate  = (str) => { const [y, m, d] = str.split("T")[0].split("-"); return new Date(+y, +m - 1, +d); };
const dateKey    = (d)   => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const today      = ()    => new Date();
const todayISO   = ()    => dateKey(today());
const formatFecha = (str) => {
  if (!str || str === "0000-00-00") return "—";
  return new Date(str).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const DIAS  = ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];
const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const TIPO_TAREA_ICONS = { llamada:"📞", visita:"🏢", enviar_cotizacion:"📄", seguimiento:"🔄", administrativo:"📋", otro:"📌" };
const TAREA_PRIORIDAD_COLOR = { alta:"#dc2626", media:"#7c3aed", baja:"#3b82f6" };

const getUserId = () => {
  try { const u = localStorage.getItem("user"); return u ? JSON.parse(u).id : null; } catch { return null; }
};

const deliveryColor = (fechaStr) => {
  const hoy  = today();
  const fecha = isoToDate(fechaStr);
  const diff  = Math.ceil((fecha - hoy) / 86400000);
  if (diff < 0)  return { dot: "#dc2626", bg: "#fef2f2", text: "#991b1b" };
  if (diff <= 3) return { dot: "#f59e0b", bg: "#fffbeb", text: "#92400e" };
  return             { dot: "#16a34a", bg: "#f0fdf4", text: "#166534" };
};

// ─── Estilos ──────────────────────────────────────────────────────────────────
const S = {
  page:  { minHeight: "100vh", background: "#f7f4f6", padding: "28px 20px", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#121212" },
  inner: { maxWidth: 1200, margin: "0 auto" },

  greeting: { fontSize: 24, fontWeight: 700, marginBottom: 4 },
  subGreet: { fontSize: 14, color: "#888", marginBottom: 28 },

  // Quick access
  qaRow:  { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 },
  qaCard: (color) => ({
    background: "#fff", borderRadius: 14, padding: "22px 24px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: `1.5px solid ${color}20`,
    cursor: "pointer", transition: "box-shadow 0.15s, transform 0.1s",
    display: "flex", flexDirection: "column", gap: 8,
  }),
  qaIcon:  (color) => ({ width: 44, height: 44, borderRadius: 12, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, marginBottom: 4 }),
  qaTitle: (color) => ({ fontSize: 15, fontWeight: 700, color }),
  qaSub:   { fontSize: 12, color: "#888" },

  // Calendar
  calWrapper:  { display: "grid", gridTemplateColumns: "1fr 300px", gap: 20, marginBottom: 32, alignItems: "start" },
  calCard:    { background: "#fff", borderRadius: 14, padding: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1.5px solid #f0e6ec" },
  calHeader:  { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  calTitle:   { fontSize: 18, fontWeight: 700 },
  calNavBtn:  { background: "none", border: "1.5px solid #e0d6dc", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 16, color: "#9f0059" },
  calGrid:    { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 },
  calDayName: { textAlign: "center", fontSize: 11, fontWeight: 700, color: "#888", padding: "4px 0", textTransform: "uppercase", letterSpacing: "0.08em" },
  calDayCell: (isToday, isOtherMonth, hasOrders, isSelected) => ({
    borderRadius: 8, minHeight: 80, padding: "6px 5px 4px",
    background: isSelected ? "#9f0059" : isToday ? "#fdf0f6" : "#fff",
    border: isToday ? "1.5px solid #9f0059" : "1.5px solid transparent",
    cursor: hasOrders ? "pointer" : "default",
    opacity: isOtherMonth ? 0.35 : 1,
    transition: "background 0.1s", position: "relative",
    overflow: "hidden",
  }),
  calDayNum: (isToday, isSelected) => ({
    fontSize: 13, fontWeight: isToday || isSelected ? 700 : 400,
    color: isSelected ? "#fff" : isToday ? "#9f0059" : "#333", marginBottom: 3,
  }),
  calBar: (color, isSelected) => ({
    background: isSelected ? "rgba(255,255,255,0.22)" : color + "22",
    borderLeft: `3px solid ${isSelected ? "rgba(255,255,255,0.85)" : color}`,
    borderRadius: 3,
    padding: "0 4px",
    fontSize: 10,
    color: isSelected ? "#fff" : color,
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: "15px",
    maxWidth: "100%",
  }),

  // Upcoming events panel
  upcomingCard: {
    background: "#fff", borderRadius: 14, padding: "20px 18px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1.5px solid #f0e6ec",
    maxHeight: 540, overflowY: "auto",
  },

  dayPopup:      { marginTop: 16, padding: 16, background: "#fdf0f6", borderRadius: 12, border: "1.5px solid #f0e6ec" },
  dayPopupTitle: { fontSize: 14, fontWeight: 700, color: "#9f0059", marginBottom: 12 },
  orderRow:    (color) => ({ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: color.bg, border: `1px solid ${color.dot}40`, borderRadius: 8, marginBottom: 6 }),
  orderRowDot: (color) => ({ width: 10, height: 10, borderRadius: "50%", background: color.dot, marginTop: 3, flexShrink: 0 }),

  // ── Entregadas widget ──────────────────────────────────────────────────────
  entSection: {
    marginTop: 28, background: "#fff", borderRadius: 14,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1.5px solid #f0e6ec",
    overflow: "hidden",
  },
  entHeader: {
    padding: "20px 24px 16px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexWrap: "wrap", gap: 12, borderBottom: "1px solid #f5f0f3",
  },
  entTitle:   { fontSize: 17, fontWeight: 700, color: "#121212", margin: 0 },
  entFilters: { display: "flex", gap: 8, flexWrap: "wrap", padding: "14px 24px", borderBottom: "1px solid #f5f0f3", background: "#fdf8fb" },
  entSelect:  { padding: "7px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 13, color: "#333", background: "#fff", outline: "none", cursor: "pointer" },
  entTh: {
    background: "#9f0059", color: "#fff",
    padding: "10px 14px", fontSize: 11, fontWeight: 700,
    letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left", whiteSpace: "nowrap",
  },
  entTd:  { padding: "10px 14px", fontSize: 13, color: "#222", borderBottom: "1px solid #f5f0f3", verticalAlign: "middle" },
  entRow: (i) => ({ background: i % 2 === 0 ? "#fff" : "#fdf8fb", cursor: "pointer", transition: "background 0.1s" }),
  entEmpty: { textAlign: "center", padding: "40px", color: "#aaa", fontSize: 14 },
  insumoCell: { maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, color: "#555" },

  // Modal
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  modal:   { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 600, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" },
  modalLg: { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 780, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" },
  mHeader: (color) => ({ background: color, padding: "20px 24px", borderRadius: "16px 16px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }),
  mTitle:  { color: "#fff", fontSize: 17, fontWeight: 700, margin: 0 },
  mClose:  { background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", lineHeight: 1 },
  mBody:   { padding: "24px" },
  mLabel:  { fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4, display: "block" },
  mSelect: { width: "100%", padding: "9px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 14, color: "#121212", background: "#fafafa", outline: "none", marginBottom: 16 },
  mInput:  { width: "100%", padding: "9px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 14, color: "#121212", background: "#fafafa", outline: "none", marginBottom: 16, boxSizing: "border-box" },
  mTextarea: { width: "100%", padding: "9px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 14, color: "#121212", background: "#fafafa", outline: "none", resize: "vertical", minHeight: 80, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 16 },
  mBtn:     (color) => ({ padding: "10px 24px", background: color, color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }),
  mBtnGhost: { padding: "10px 20px", background: "transparent", color: "#555", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", marginRight: 8 },

  // Plantilla card
  plantillaCard:  (selected) => ({ padding: "12px 16px", border: `2px solid ${selected ? "#1d4ed8" : "#e0d6dc"}`, borderRadius: 10, cursor: "pointer", marginBottom: 8, background: selected ? "#eff6ff" : "#fafafa", transition: "border-color 0.15s" }),
  plantillaTitle: { fontWeight: 700, fontSize: 14, color: "#121212" },
  plantillaMeta:  { fontSize: 12, color: "#888", marginTop: 2 },

  // Info grid popup entregadas
  infoGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", marginBottom: 20 },
  infoItem: { fontSize: 13 },
  infoLabel: { fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 },
  infoValue: { color: "#121212", fontWeight: 500 },
};

// ─── Componente principal ─────────────────────────────────────────────────────
const Inicio = () => {
  const { user } = useContext(UserContext);
  const history  = useHistory();

  // ── Calendario ───────────────────────────────────────────────────────────
  const [viewDate,    setViewDate]    = useState(today());
  const [ordersMap,   setOrdersMap]   = useState({});
  const [tareasMap,   setTareasMap]   = useState({});
  const [selectedDay, setSelectedDay] = useState(null);

  // ── Modal OC ─────────────────────────────────────────────────────────────
  const [showOC,        setShowOC]        = useState(false);
  const [ocStep,        setOcStep]        = useState(1);
  const [ocClientes,    setOcClientes]    = useState([]);
  const [ocProveedores, setOcProveedores] = useState([]);
  const [ocContactos,   setOcContactos]   = useState([]);
  const [ocClienteId,   setOcClienteId]   = useState("");
  const [ocProveedorId, setOcProveedorId] = useState("");
  const [ocContactoId,  setOcContactoId]  = useState("");
  const [ocRecientes,   setOcRecientes]   = useState([]);
  const [ocPlantilla,   setOcPlantilla]   = useState(null);
  const [ocLoadingRec,  setOcLoadingRec]  = useState(false);

  // ── Modal Tarea rápida (crear) ────────────────────────────────────────────
  const [showTarea,   setShowTarea]   = useState(false);
  const [tareaForm,   setTareaForm]   = useState({ titulo: "", tipo: "seguimiento", prioridad: "media", vencimiento: "", descripcion: "" });
  const [tareaSaving, setTareaSaving] = useState(false);

  // ── Modal detalle / editar tarea ─────────────────────────────────────────
  const [tareaDetalle,      setTareaDetalle]      = useState(null);
  const [tareaDetalleForm,  setTareaDetalleForm]  = useState({});
  const [savingTareaDetalle, setSavingTareaDetalle] = useState(false);

  // ── Modal Interacción ─────────────────────────────────────────────────────
  const [showInt,       setShowInt]       = useState(false);
  const [intClientes,   setIntClientes]   = useState([]);
  const [intContactos,  setIntContactos]  = useState([]);
  const [intClienteId,  setIntClienteId]  = useState("");
  const [intContactoId, setIntContactoId] = useState("");
  const [intForm,   setIntForm]   = useState({ fecha: todayISO(), canal: "whatsapp", resumen: "", proximo_paso: "" });
  const [intSaving, setIntSaving] = useState(false);

  // ── Entregadas widget ─────────────────────────────────────────────────────
  const [entregadas,    setEntregadas]    = useState([]);
  const [loadingEnt,    setLoadingEnt]    = useState(true);
  const [mesesEnt,      setMesesEnt]      = useState(3);
  const [filtCliente,   setFiltCliente]   = useState("");
  const [filtProveedor, setFiltProveedor] = useState("");
  const [filtUsuario,   setFiltUsuario]   = useState("");
  const [entDetalle,    setEntDetalle]    = useState(null);   // orden seleccionada (popup)
  const [entItems,      setEntItems]      = useState([]);     // ítems del popup
  const [loadingItems,  setLoadingItems]  = useState(false);

  // ── Cargar órdenes con fecha de entrega (calendario) ─────────────────────
  useEffect(() => {
    axios.post(`${BASE}/buscar_fechas_entrega.php`).then((r) => {
      if (!Array.isArray(r.data)) return;
      const map = {};
      r.data.forEach((o) => {
        if (!o.fecha_entrega) return;
        const k = o.fecha_entrega.split("T")[0];
        if (!map[k]) map[k] = [];
        map[k].push(o);
      });
      setOrdersMap(map);
    }).catch(() => {});
  }, []);

  // ── Cargar tareas activas del usuario (calendario) ────────────────────────
  const loadTareasCalendario = useCallback(() => {
    const uid = getUserId();
    if (!uid) return;
    const fd = new FormData();
    fd.append("usuario_id", uid);
    fd.append("estado", "activas");
    axios.post(`${BASE}/tareas_listar.php`, fd).then((r) => {
      if (!Array.isArray(r.data)) return;
      const map = {};
      r.data.forEach((t) => {
        if (!t.vencimiento) return;
        const k = t.vencimiento.split(" ")[0].split("T")[0];
        if (!map[k]) map[k] = [];
        map[k].push(t);
      });
      setTareasMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadTareasCalendario(); }, [loadTareasCalendario]);

  // ── Cargar entregadas cuando cambia el período ────────────────────────────
  useEffect(() => {
    setLoadingEnt(true);
    setFiltCliente(""); setFiltProveedor(""); setFiltUsuario("");
    axios.post(`${BASE}/buscar_entregadas_inicio.php`, new URLSearchParams({ meses: mesesEnt }))
      .then((r) => { if (Array.isArray(r.data)) setEntregadas(r.data); })
      .catch(() => {})
      .finally(() => setLoadingEnt(false));
  }, [mesesEnt]);

  // ── Cargar ítems cuando se abre el popup ─────────────────────────────────
  useEffect(() => {
    if (!entDetalle) { setEntItems([]); return; }
    setLoadingItems(true);
    axios.post(`${BASE}/buscar_items_plantilla.php`, new URLSearchParams({ orden_id: entDetalle.id }))
      .then((r) => setEntItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEntItems([]))
      .finally(() => setLoadingItems(false));
  }, [entDetalle]);

  // ── Cargar clientes/proveedores ───────────────────────────────────────────
  const loadClientes = useCallback(() => {
    axios.post(`${BASE}/buscar_terceros.php`).then((r) => {
      const clientes = r.data.filter((x) => x.tipo_other === 2);
      setOcClientes(clientes);
      setIntClientes(clientes);
      setOcProveedores(r.data.filter((x) => x.tipo_other === 3));
    }).catch(() => {});
  }, []);

  // ── OC: contactos al cambiar cliente ─────────────────────────────────────
  useEffect(() => {
    if (!ocClienteId) { setOcContactos([]); setOcContactoId(""); return; }
    axios.post(`${BASE}/crm_contactos_listar.php`, new URLSearchParams({ cliente_id: ocClienteId }))
      .then((r) => setOcContactos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOcContactos([]));
  }, [ocClienteId]);

  // ── OC: órdenes recientes al pasar al step 2 ─────────────────────────────
  useEffect(() => {
    if (ocStep !== 2 || !ocClienteId) return;
    setOcLoadingRec(true);
    axios.post(`${BASE}/buscar_ordenes_recientes_cliente.php`, new URLSearchParams({ cliente_id: ocClienteId }))
      .then((r) => setOcRecientes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOcRecientes([]))
      .finally(() => setOcLoadingRec(false));
  }, [ocStep, ocClienteId]);

  // ── Int: contactos al cambiar cliente ────────────────────────────────────
  useEffect(() => {
    if (!intClienteId) { setIntContactos([]); setIntContactoId(""); return; }
    axios.post(`${BASE}/crm_contactos_listar.php`, new URLSearchParams({ cliente_id: intClienteId }))
      .then((r) => setIntContactos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setIntContactos([]));
  }, [intClienteId]);

  // ── Calendario helpers ────────────────────────────────────────────────────
  const prevMonth = () => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const getDaysInMonth = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay  = new Date(year, month + 1, 0);
    let start = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = start - 1; i >= 0; i--) days.push({ date: new Date(year, month, -i), otherMonth: true });
    for (let d = 1; d <= lastDay.getDate(); d++) days.push({ date: new Date(year, month, d), otherMonth: false });
    while (days.length % 7 !== 0) days.push({ date: new Date(year, month + 1, days.length - lastDay.getDate() - start + 1), otherMonth: true });
    return days;
  };

  const calDays  = getDaysInMonth(viewDate.getFullYear(), viewDate.getMonth());
  const todayKey = dateKey(today());

  // ── Próximos eventos (para el panel lateral) ──────────────────────────────
  const upcomingEvents = useMemo(() => {
    const events = [];
    Object.entries(ordersMap).forEach(([dk, ords]) => {
      if (dk >= todayKey)
        ords.forEach((o) => events.push({
          type: "order", dateKey: dk,
          label: o.cliente || `#${o.id}`,
          color: deliveryColor(o.fecha_entrega).dot,
          sub: o.proveedor,
          data: o,
        }));
    });
    Object.entries(tareasMap).forEach(([dk, tasks]) => {
      if (dk >= todayKey)
        tasks.forEach((t) => events.push({
          type: "task", dateKey: dk,
          label: t.titulo,
          color: TAREA_PRIORIDAD_COLOR[t.prioridad] || "#7c3aed",
          sub: t.cliente_nombre,
          data: t,
        }));
    });
    return events.sort((a, b) => a.dateKey.localeCompare(b.dateKey)).slice(0, 25);
  }, [ordersMap, tareasMap, todayKey]);

  // ── Acciones rápidas ──────────────────────────────────────────────────────
  const openOC = () => {
    loadClientes();
    setOcStep(1); setOcClienteId(""); setOcProveedorId(""); setOcContactoId("");
    setOcPlantilla(null); setOcRecientes([]);
    setShowOC(true);
  };
  const openInt = () => {
    loadClientes();
    setIntClienteId(""); setIntContactoId("");
    setIntForm({ fecha: todayISO(), canal: "whatsapp", resumen: "", proximo_paso: "" });
    setShowInt(true);
  };

  const openTarea = () => {
    setTareaForm({ titulo: "", tipo: "seguimiento", prioridad: "media", vencimiento: "", descripcion: "" });
    setShowTarea(true);
  };

  const handleTareaSubmit = async (e) => {
    e.preventDefault();
    if (!tareaForm.titulo.trim() || !tareaForm.vencimiento) {
      toast.warning("Título y vencimiento son requeridos.");
      return;
    }
    const uid = getUserId() ?? user?.id;
    if (!uid) { toast.error("Sesión inválida."); return; }
    setTareaSaving(true);
    try {
      const fd = new FormData();
      Object.entries({ ...tareaForm, usuario_id: uid, creado_por: uid })
        .forEach(([k, v]) => fd.append(k, v ?? ""));
      const res = await axios.post(`${BASE}/tareas_guardar.php`, fd);
      if (res.data?.success) {
        toast.success("Tarea creada ✓");
        setShowTarea(false);
        loadTareasCalendario();
      } else {
        toast.error(res.data?.mensaje || "Error al crear la tarea.");
      }
    } catch { toast.error("Error de conexión."); }
    finally  { setTareaSaving(false); }
  };

  // Sync edit form when a task is selected for detail view
  useEffect(() => {
    if (!tareaDetalle) return;
    const ven = tareaDetalle.vencimiento
      ? tareaDetalle.vencimiento.replace(" ", "T").slice(0, 16)
      : "";
    setTareaDetalleForm({
      id:          tareaDetalle.id,
      titulo:      tareaDetalle.titulo      || "",
      descripcion: tareaDetalle.descripcion || "",
      tipo:        tareaDetalle.tipo        || "otro",
      prioridad:   tareaDetalle.prioridad   || "media",
      estado:      tareaDetalle.estado      || "pendiente",
      vencimiento: ven,
      usuario_id:  tareaDetalle.usuario_id  || "",
      cliente_id:  tareaDetalle.cliente_id  || "",
      contacto_id: tareaDetalle.contacto_id || "",
      oportunidad_id: tareaDetalle.oportunidad_id || "",
      orden_id:    tareaDetalle.orden_id    || "",
    });
  }, [tareaDetalle]);

  const handleTareaDetalleSubmit = async (e) => {
    e.preventDefault();
    if (!tareaDetalleForm.titulo.trim() || !tareaDetalleForm.vencimiento) {
      toast.warning("Título y vencimiento son requeridos.");
      return;
    }
    setSavingTareaDetalle(true);
    try {
      const fd = new FormData();
      Object.entries(tareaDetalleForm).forEach(([k, v]) => fd.append(k, v ?? ""));
      const res = await axios.post(`${BASE}/tareas_guardar.php`, fd);
      if (res.data?.success) {
        toast.success("Tarea actualizada ✓");
        setTareaDetalle(null);
        loadTareasCalendario();
      } else {
        toast.error(res.data?.mensaje || "Error al guardar.");
      }
    } catch { toast.error("Error de conexión."); }
    finally  { setSavingTareaDetalle(false); }
  };

  const completarTarea = async (tarea) => {
    setSavingTareaDetalle(true);
    try {
      const fd = new FormData();
      const ven = tarea.vencimiento ? tarea.vencimiento.replace(" ", "T").slice(0, 16) : "";
      Object.entries({ ...tarea, vencimiento: ven, estado: "hecha" })
        .forEach(([k, v]) => fd.append(k, v ?? ""));
      const res = await axios.post(`${BASE}/tareas_guardar.php`, fd);
      if (res.data?.success) {
        toast.success("¡Tarea completada! ✓");
        setTareaDetalle(null);
        loadTareasCalendario();
      } else {
        toast.error(res.data?.mensaje || "Error al completar.");
      }
    } catch { toast.error("Error de conexión."); }
    finally  { setSavingTareaDetalle(false); }
  };

  const ocContinuar = () => {
    if (!ocClienteId || !ocContactoId) { toast.warning("Seleccioná cliente y contacto."); return; }
    setOcStep(2);
  };

  const ocIrAlForm = (plantilla) => {
    sessionStorage.setItem("nueva_oc_preload", JSON.stringify({
      clienteId:    ocClienteId,
      contactoId:   ocContactoId,
      proveedorId:  plantilla?.proveedor_id || "",
      plantillaId:  plantilla?.id || null,
      observaciones: plantilla?.observaciones || "",
    }));
    setShowOC(false);
    history.push("/dashboard/nueva-oc");
  };

  // ── Repetir orden (desde popup entregadas) ────────────────────────────────
  const repetirOrden = (orden) => {
    sessionStorage.setItem("nueva_oc_preload", JSON.stringify({
      clienteId:   orden.cliente_id,
      contactoId:  orden.contacto_id || "",
      proveedorId: orden.proveedor_id || "",
      plantillaId: orden.id,
      observaciones: "",
    }));
    setEntDetalle(null);
    history.push("/dashboard/nueva-oc");
  };

  // ── Interacción: guardar ──────────────────────────────────────────────────
  const handleIntSubmit = async (e) => {
    e.preventDefault();
    if (!intClienteId)  { toast.warning("Seleccioná un cliente.");  return; }
    if (!intContactoId) { toast.warning("Seleccioná un contacto."); return; }
    if (!intForm.resumen.trim()) { toast.warning("El resumen es requerido."); return; }
    setIntSaving(true);
    try {
      const fd = new FormData();
      fd.append("cliente_id",   intClienteId);
      fd.append("contacto_id",  intContactoId);
      fd.append("usuario_id",   getUserId() ?? user?.id ?? "");
      fd.append("canal",        intForm.canal);
      fd.append("fecha",        intForm.fecha);
      fd.append("resumen",      intForm.resumen);
      fd.append("proximo_paso", intForm.proximo_paso);
      const res = await axios.post(`${BASE}/crm_interacciones_guardar.php`, fd);
      if (res.data.success) { toast.success("Interacción registrada ✓"); setShowInt(false); }
      else toast.error(res.data.mensaje || "Error al guardar.");
    } catch { toast.error("Error de conexión."); }
    finally  { setIntSaving(false); }
  };

  // ── Saludo ────────────────────────────────────────────────────────────────
  const hora   = new Date().getHours();
  const saludo = hora < 12 ? "¡Buenos días" : hora < 19 ? "¡Buenas tardes" : "¡Buenas noches";
  const nombre = user?.nombre || user?.name || "usuario";

  // ── Filtros entregadas (client-side) ──────────────────────────────────────
  const optsClientes   = [...new Map(entregadas.filter(o => o.cliente_id).map(o => [o.cliente_id,   o.cliente])).entries()].sort((a,b) => a[1].localeCompare(b[1]));
  const optsProveedores= [...new Map(entregadas.filter(o => o.proveedor_id).map(o => [o.proveedor_id, o.proveedor])).entries()].sort((a,b) => a[1].localeCompare(b[1]));
  const optsUsuarios   = [...new Map(entregadas.filter(o => o.usuario_id).map(o => [o.usuario_id,   o.usuario])).entries()].sort((a,b) => a[1].localeCompare(b[1]));

  const entFiltradas = entregadas.filter((o) =>
    (!filtCliente   || String(o.cliente_id)   === filtCliente)   &&
    (!filtProveedor || String(o.proveedor_id) === filtProveedor) &&
    (!filtUsuario   || String(o.usuario_id)   === filtUsuario)
  );

  return (
    <div style={S.page}>
      <div style={S.inner}>

        {/* ── Saludo ── */}
        <div>
          <h1 style={S.greeting}>{saludo}, {nombre}! 👋</h1>
          <p style={S.subGreet}>
            {DIAS[(today().getDay() + 6) % 7]}, {today().getDate()} de {MESES[today().getMonth()]} {today().getFullYear()}
          </p>
        </div>

        {/* ── Accesos rápidos ── */}
        <div style={S.qaRow}>
          <div style={S.qaCard("#9f0059")} onClick={() => history.push("/dashboard/newquotation")}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(159,0,89,0.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)";    e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={S.qaIcon("#9f0059")}>📝</div>
            <div style={S.qaTitle("#9f0059")}>Nueva Cotización</div>
            <div style={S.qaSub}>Crear una cotización para un cliente</div>
          </div>

          <div style={S.qaCard("#1d4ed8")} onClick={openOC}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(29,78,216,0.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)";     e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={S.qaIcon("#1d4ed8")}>📋</div>
            <div style={S.qaTitle("#1d4ed8")}>Nueva Orden de Compra</div>
            <div style={S.qaSub}>Cargar una OC directa sin pasar por cotización</div>
          </div>

          <div style={S.qaCard("#059669")} onClick={openInt}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(5,150,105,0.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)";     e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={S.qaIcon("#059669")}>💬</div>
            <div style={S.qaTitle("#059669")}>Nueva Interacción</div>
            <div style={S.qaSub}>Registrar una reunión, llamada o mensaje</div>
          </div>

          <div style={S.qaCard("#7c3aed")} onClick={openTarea}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(124,58,237,0.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)";      e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={S.qaIcon("#7c3aed")}>✅</div>
            <div style={S.qaTitle("#7c3aed")}>Nueva Tarea</div>
            <div style={S.qaSub}>Crear un recordatorio o tarea de seguimiento</div>
          </div>
        </div>

        {/* ── Calendario + Próximos eventos ── */}
        <div style={S.calWrapper}>
        <div style={S.calCard}>
          <div style={S.calHeader}>
            <button style={S.calNavBtn} onClick={prevMonth}>‹</button>
            <span style={S.calTitle}>{MESES[viewDate.getMonth()]} {viewDate.getFullYear()}</span>
            <button style={S.calNavBtn} onClick={nextMonth}>›</button>
          </div>
          <div style={S.calGrid}>
            {DIAS.map((d) => <div key={d} style={S.calDayName}>{d}</div>)}
          </div>
          <div style={{ ...S.calGrid, marginTop: 4 }}>
            {calDays.map(({ date, otherMonth }, i) => {
              const key       = dateKey(date);
              const orders    = ordersMap[key] || [];
              const taskItems = tareasMap[key] || [];
              const hasEvents = orders.length > 0 || taskItems.length > 0;
              const isToday   = key === todayKey;
              const isSel     = key === selectedDay;
              return (
                <div key={i} style={S.calDayCell(isToday, otherMonth, hasEvents, isSel)}
                  onClick={() => hasEvents && setSelectedDay(isSel ? null : key)}>
                  <div style={S.calDayNum(isToday, isSel)}>{date.getDate()}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {orders.slice(0, 2).map((o, j) => (
                      <div key={`o${j}`} style={S.calBar(deliveryColor(o.fecha_entrega).dot, isSel)}>
                        {o.cliente || `#${o.id}`}
                      </div>
                    ))}
                    {taskItems.slice(0, Math.max(0, 2 - orders.length)).map((t, j) => (
                      <div key={`t${j}`} style={S.calBar("#7c3aed", isSel)}>
                        {t.titulo}
                      </div>
                    ))}
                    {(orders.length + taskItems.length) > 2 && (
                      <div style={{ fontSize: 9, color: isSel ? "rgba(255,255,255,0.7)" : "#888", lineHeight: "13px", paddingLeft: 2 }}>
                        +{orders.length + taskItems.length - 2} más
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap" }}>
            {[
              { color: "#16a34a", label: "Entrega a tiempo" },
              { color: "#f59e0b", label: "Entrega próxima (≤3d)" },
              { color: "#dc2626", label: "Entrega vencida" },
              { color: "#7c3aed", label: "Tarea pendiente" },
            ].map(({ color, label }) => (
              <span key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#666" }}>
                <span style={{ width: 12, height: 12, background: color + "22", borderLeft: `3px solid ${color}`, borderRadius: 2, display: "inline-block" }} />
                {label}
              </span>
            ))}
          </div>
          {selectedDay && (ordersMap[selectedDay] || tareasMap[selectedDay]) && (
            <div style={S.dayPopup}>
              <div style={S.dayPopupTitle}>
                📅 {isoToDate(selectedDay).toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" })}
              </div>

              {/* Órdenes */}
              {(ordersMap[selectedDay] || []).map((o) => {
                const c = deliveryColor(o.fecha_entrega);
                return (
                  <div key={o.id} style={S.orderRow(c)}>
                    <div style={S.orderRowDot(c)} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>#{o.id} — {o.cliente}</div>
                      <div style={{ fontSize: 12, color: "#666" }}>{o.proveedor}{o.monto_total ? ` · $${parseFloat(o.monto_total).toLocaleString("es-AR")}` : ""}</div>
                      {o.observaciones && <div style={{ fontSize: 11, color: "#888", marginTop: 2, fontStyle: "italic" }}>{o.observaciones.slice(0, 80)}{o.observaciones.length > 80 ? "…" : ""}</div>}
                    </div>
                  </div>
                );
              })}

              {/* Tareas */}
              {(tareasMap[selectedDay] || []).map((t) => {
                const pc = TAREA_PRIORIDAD_COLOR[t.prioridad] || "#7c3aed";
                return (
                  <div key={t.id}
                    onClick={() => setTareaDetalle(t)}
                    style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, marginBottom: 6, cursor: "pointer", transition: "background 0.12s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#ede9fe"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#f5f3ff"; }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: pc, marginTop: 4, flexShrink: 0, display: "inline-block" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#4c1d95" }}>
                        {TIPO_TAREA_ICONS[t.tipo] || "📌"} {t.titulo}
                      </div>
                      <div style={{ fontSize: 12, color: "#6d28d9", marginTop: 2 }}>
                        {t.vencimiento ? new Date(t.vencimiento).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : ""}
                        {t.cliente_nombre ? ` · ${t.cliente_nombre}` : ""}
                        {` · ${t.prioridad}`}
                      </div>
                      {t.descripcion && <div style={{ fontSize: 11, color: "#888", marginTop: 2, fontStyle: "italic" }}>{t.descripcion.slice(0, 80)}{t.descripcion.length > 80 ? "…" : ""}</div>}
                    </div>
                    <span style={{ fontSize: 11, color: "#7c3aed", flexShrink: 0, marginTop: 2 }}>Ver →</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>{/* /calCard */}

        {/* ── Panel: Próximos eventos ── */}
        <div style={S.upcomingCard}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#9f0059", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            📅 Próximos eventos
          </div>
          {upcomingEvents.length === 0 ? (
            <p style={{ color: "#bbb", fontSize: 13, margin: 0 }}>Sin eventos próximos</p>
          ) : (() => {
            const todayStr   = dateKey(today());
            const tomorrowStr = dateKey(new Date(Date.now() + 86400000));
            let lastDate = null;
            return upcomingEvents.map((ev, i) => {
              const showHeader = ev.dateKey !== lastDate;
              lastDate = ev.dateKey;
              const headerLabel = ev.dateKey === todayStr ? "Hoy"
                : ev.dateKey === tomorrowStr ? "Mañana"
                : isoToDate(ev.dateKey).toLocaleDateString("es-AR", { weekday: "short", day: "numeric", month: "short" });
              return (
                <React.Fragment key={`${ev.type}-${ev.data.id}-${i}`}>
                  {showHeader && (
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#aaa", textTransform: "uppercase",
                      letterSpacing: "0.08em", marginTop: i === 0 ? 0 : 10, marginBottom: 4 }}>
                      {headerLabel}
                    </div>
                  )}
                  <div
                    onClick={() => ev.type === "task" ? setTareaDetalle(ev.data) : setSelectedDay(ev.dateKey)}
                    style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 7px",
                      borderRadius: 7, cursor: "pointer", marginBottom: 1,
                      background: "transparent", transition: "background 0.1s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f7f4f6"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: ev.type === "task" ? 2 : "50%",
                      background: ev.color, flexShrink: 0, marginTop: 3 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#222",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {ev.type === "order" ? "📦 " : "✅ "}{ev.label}
                      </div>
                      {ev.sub && (
                        <div style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap",
                          overflow: "hidden", textOverflow: "ellipsis" }}>{ev.sub}</div>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            });
          })()}
        </div>{/* /upcomingCard */}

        </div>{/* /calWrapper */}

        {/* ══════════════════════════════════════════════════════
            HISTORIAL DE ÓRDENES ENTREGADAS
        ══════════════════════════════════════════════════════ */}
        <div style={S.entSection}>

          {/* Header del widget */}
          <div style={S.entHeader}>
            <h2 style={S.entTitle}>Historial de órdenes entregadas</h2>
            <select style={S.entSelect} value={mesesEnt} onChange={(e) => setMesesEnt(parseInt(e.target.value))}>
              <option value={1}>Último mes</option>
              <option value={3}>Últimos 3 meses</option>
              <option value={6}>Últimos 6 meses</option>
              <option value={12}>Último año</option>
              <option value={999}>Todo el historial</option>
            </select>
          </div>

          {/* Filtros */}
          <div style={S.entFilters}>
            <select style={S.entSelect} value={filtCliente} onChange={(e) => setFiltCliente(e.target.value)}>
              <option value="">Todos los clientes</option>
              {optsClientes.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
            </select>
            <select style={S.entSelect} value={filtProveedor} onChange={(e) => setFiltProveedor(e.target.value)}>
              <option value="">Todos los proveedores</option>
              {optsProveedores.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
            </select>
            <select style={S.entSelect} value={filtUsuario} onChange={(e) => setFiltUsuario(e.target.value)}>
              <option value="">Todos los usuarios</option>
              {optsUsuarios.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
            </select>
            {(filtCliente || filtProveedor || filtUsuario) && (
              <button
                onClick={() => { setFiltCliente(""); setFiltProveedor(""); setFiltUsuario(""); }}
                style={{ ...S.entSelect, background: "#fdf0f6", border: "1.5px solid #f0c0d8", color: "#9f0059", cursor: "pointer", fontWeight: 600 }}
              >
                ✕ Limpiar filtros
              </button>
            )}
            <span style={{ marginLeft: "auto", fontSize: 12, color: "#999", alignSelf: "center" }}>
              {loadingEnt ? "Cargando…" : `${entFiltradas.length} orden${entFiltradas.length !== 1 ? "es" : ""}`}
            </span>
          </div>

          {/* Tabla */}
          {loadingEnt ? (
            <p style={S.entEmpty}>Cargando órdenes…</p>
          ) : entFiltradas.length === 0 ? (
            <p style={S.entEmpty}>No hay órdenes entregadas en este período.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Cliente","Proveedor","Insumos","Usuario","Monto","Fecha cotización","Fecha entrega"].map((h) => (
                      <th key={h} style={S.entTh}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {entFiltradas.map((o, i) => (
                    <tr key={o.id} style={S.entRow(i)}
                      onClick={() => setEntDetalle(o)}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#fdf0f6"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fdf8fb"; }}
                    >
                      <td style={{ ...S.entTd, fontWeight: 600 }}>{o.cliente}</td>
                      <td style={S.entTd}>{o.proveedor}</td>
                      <td style={S.entTd}>
                        <span style={S.insumoCell} title={o.insumos_preview}>
                          {o.insumos_preview ? (o.insumos_preview.length > 60 ? o.insumos_preview.slice(0, 60) + "…" : o.insumos_preview) : "—"}
                        </span>
                      </td>
                      <td style={S.entTd}>{o.usuario}</td>
                      <td style={{ ...S.entTd, fontWeight: 600 }}>
                        {o.monto_total ? (
                          <span>
                            {o.moneda === "USD" ? "USD " : "$"}
                            {parseFloat(o.monto_total).toLocaleString("es-AR")}
                            {o.moneda === "USD" && (
                              <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700, padding: "1px 5px",
                                borderRadius: 20, background: "#fffbeb", color: "#92400e", border: "1px solid #fcd34d" }}>
                                USD
                              </span>
                            )}
                          </span>
                        ) : "—"}
                      </td>
                      <td style={S.entTd}>{formatFecha(o.created_at)}</td>
                      <td style={S.entTd}>{formatFecha(o.fecha_entrega)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>{/* /inner */}

      {/* ═══════════════════════════════════════════════════════
          POPUP: Detalle de orden entregada
      ═══════════════════════════════════════════════════════ */}
      {entDetalle && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setEntDetalle(null)}>
          <div style={S.modalLg}>
            <div style={S.mHeader("#9f0059")}>
              <span style={S.mTitle}>Orden #{entDetalle.id} — {entDetalle.cliente}</span>
              <button style={S.mClose} onClick={() => setEntDetalle(null)}>×</button>
            </div>
            <div style={S.mBody}>

              {/* Info general */}
              <div style={S.infoGrid}>
                <div style={S.infoItem}>
                  <div style={S.infoLabel}>Cliente</div>
                  <div style={S.infoValue}>{entDetalle.cliente}</div>
                </div>
                <div style={S.infoItem}>
                  <div style={S.infoLabel}>Proveedor</div>
                  <div style={S.infoValue}>{entDetalle.proveedor}</div>
                </div>
                <div style={S.infoItem}>
                  <div style={S.infoLabel}>Usuario</div>
                  <div style={S.infoValue}>{entDetalle.usuario}</div>
                </div>
                <div style={S.infoItem}>
                  <div style={S.infoLabel}>Monto total</div>
                  <div style={{ ...S.infoValue, color: "#059669", fontWeight: 700, fontSize: 16 }}>
                    {entDetalle.monto_total ? `$${parseFloat(entDetalle.monto_total).toLocaleString("es-AR")}` : "—"}
                  </div>
                </div>
                <div style={S.infoItem}>
                  <div style={S.infoLabel}>Fecha cotización</div>
                  <div style={S.infoValue}>{formatFecha(entDetalle.created_at)}</div>
                </div>
                <div style={S.infoItem}>
                  <div style={S.infoLabel}>Fecha entrega</div>
                  <div style={S.infoValue}>{formatFecha(entDetalle.fecha_entrega)}</div>
                </div>
              </div>

              {/* Tabla de ítems */}
              <div style={{ fontWeight: 700, fontSize: 13, color: "#9f0059", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Productos
              </div>
              {loadingItems ? (
                <p style={{ color: "#aaa", fontSize: 13 }}>Cargando productos…</p>
              ) : entItems.length === 0 ? (
                <p style={{ color: "#aaa", fontSize: 13 }}>Sin productos registrados.</p>
              ) : (
                <div style={{ border: "1px solid #f0e6ec", borderRadius: 8, overflow: "hidden", marginBottom: 20 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Descripción","N° Plano","Categoría","Cantidad","Precio","Observaciones"].map((h) => (
                          <th key={h} style={{ ...S.entTh, fontSize: 10, padding: "8px 12px" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {entItems.map((item, i) => (
                        <tr key={item.id} style={{ background: i % 2 === 0 ? "#fff" : "#fdf8fb" }}>
                          <td style={{ ...S.entTd, padding: "8px 12px", fontWeight: 500 }}>{item.description || "—"}</td>
                          <td style={{ ...S.entTd, padding: "8px 12px" }}>{item.number || "—"}</td>
                          <td style={{ ...S.entTd, padding: "8px 12px" }}>{item.category || "—"}</td>
                          <td style={{ ...S.entTd, padding: "8px 12px", textAlign: "center" }}>{item.quantity}</td>
                          <td style={{ ...S.entTd, padding: "8px 12px" }}>
                            {item.price ? `$${parseFloat(item.price).toLocaleString("es-AR")}` : "—"}
                          </td>
                          <td style={{ ...S.entTd, padding: "8px 12px", fontSize: 12, color: "#777", fontStyle: "italic" }}>{item.observations || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Botones */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button style={S.mBtnGhost} onClick={() => setEntDetalle(null)}>Cerrar</button>
                <button style={S.mBtn("#1d4ed8")} onClick={() => repetirOrden(entDetalle)}>
                  🔁 Repetir orden
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          MODAL: Nueva Orden de Compra
      ═══════════════════════════════════════════════════════ */}
      {showOC && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowOC(false)}>
          <div style={S.modal}>
            <div style={S.mHeader("#1d4ed8")}>
              <span style={S.mTitle}>{ocStep === 1 ? "📋 Nueva Orden de Compra — Cliente" : "📋 Seleccioná una plantilla"}</span>
              <button style={S.mClose} onClick={() => setShowOC(false)}>×</button>
            </div>
            <div style={S.mBody}>
              {ocStep === 1 && (
                <>
                  <label style={S.mLabel}>Cliente *</label>
                  <select style={S.mSelect} value={ocClienteId} onChange={(e) => { setOcClienteId(e.target.value); setOcContactoId(""); }}>
                    <option value="">Seleccioná un cliente</option>
                    {ocClientes.map((c) => <option key={c.id} value={c.id}>{c.tercero}</option>)}
                  </select>

                  <label style={S.mLabel}>
                    Contacto *
                    {ocClienteId && ocContactos.length === 0 && <span style={{ color: "#f59e0b", marginLeft: 8, fontWeight: 400 }}>— Sin contactos en CRM</span>}
                  </label>
                  <select style={{ ...S.mSelect, borderColor: !ocContactoId && ocClienteId ? "#f59e0b" : "#e0d6dc" }}
                    value={ocContactoId} onChange={(e) => setOcContactoId(e.target.value)}
                    disabled={!ocClienteId || ocContactos.length === 0}>
                    <option value="">{!ocClienteId ? "Primero seleccioná un cliente" : ocContactos.length === 0 ? "Sin contactos disponibles" : "Seleccioná un contacto"}</option>
                    {ocContactos.map((c) => <option key={c.id} value={c.id}>{c.nombre}{c.cargo ? ` — ${c.cargo}` : ""}</option>)}
                  </select>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                    <button style={S.mBtnGhost} onClick={() => setShowOC(false)}>Cancelar</button>
                    <button style={S.mBtn("#1d4ed8")} onClick={ocContinuar}>Continuar →</button>
                  </div>
                </>
              )}

              {ocStep === 2 && (
                <>
                  <p style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>
                    Elegí una orden anterior como plantilla para precargar los productos, o empezá en blanco.
                  </p>
                  {ocLoadingRec ? (
                    <p style={{ color: "#aaa", textAlign: "center", padding: 20 }}>Cargando órdenes recientes…</p>
                  ) : ocRecientes.length === 0 ? (
                    <p style={{ color: "#aaa", fontSize: 13, marginBottom: 16 }}>No hay órdenes anteriores para este cliente.</p>
                  ) : (
                    ocRecientes.map((o) => (
                      <div key={o.id} style={S.plantillaCard(ocPlantilla?.id === o.id)} onClick={() => setOcPlantilla(ocPlantilla?.id === o.id ? null : o)}>
                        <div style={S.plantillaTitle}>Orden #{o.id} — {o.proveedor_nombre || "Sin proveedor"}</div>
                        <div style={S.plantillaMeta}>
                          {o.total_items} producto{o.total_items !== 1 ? "s" : ""}
                          {o.items_preview ? ` · ${o.items_preview}` : ""}
                          {o.monto_total ? ` · $${parseFloat(o.monto_total).toLocaleString("es-AR")}` : ""}
                        </div>
                      </div>
                    ))
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
                    <button style={S.mBtnGhost} onClick={() => setOcStep(1)}>← Volver</button>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={S.mBtnGhost} onClick={() => ocIrAlForm(null)}>Empezar en blanco</button>
                      <button style={{ ...S.mBtn("#1d4ed8"), opacity: ocPlantilla ? 1 : 0.5, cursor: ocPlantilla ? "pointer" : "default" }}
                        onClick={() => ocPlantilla && ocIrAlForm(ocPlantilla)} disabled={!ocPlantilla}>
                        Usar esta plantilla →
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          MODAL: Detalle / Editar Tarea
      ═══════════════════════════════════════════════════════ */}
      {tareaDetalle && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setTareaDetalle(null)}>
          <div style={S.modal}>
            {/* Header */}
            <div style={{ ...S.mHeader("#7c3aed"), flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
              <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "flex-start" }}>
                <span style={{ ...S.mTitle, fontSize: 15 }}>
                  {TIPO_TAREA_ICONS[tareaDetalle.tipo] || "📌"} {tareaDetalle.titulo}
                </span>
                <button style={S.mClose} onClick={() => setTareaDetalle(null)}>×</button>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {/* Estado badge */}
                <span style={{
                  padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                  background: tareaDetalle.estado === "hecha" ? "#dcfce7" : tareaDetalle.estado === "en_curso" ? "#fef9c3" : "#ede9fe",
                  color:      tareaDetalle.estado === "hecha" ? "#166534" : tareaDetalle.estado === "en_curso" ? "#854d0e"  : "#4c1d95",
                }}>
                  {tareaDetalle.estado === "hecha" ? "✓ Finalizada" : tareaDetalle.estado === "en_curso" ? "⏳ En curso" : "⏰ Pendiente"}
                </span>
                <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "rgba(255,255,255,0.2)", color: "#fff" }}>
                  Prioridad: {tareaDetalle.prioridad}
                </span>
                {tareaDetalle.cliente_nombre && (
                  <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: "rgba(255,255,255,0.15)", color: "#fff" }}>
                    {tareaDetalle.cliente_nombre}
                  </span>
                )}
              </div>
            </div>

            <div style={S.mBody}>
              <form onSubmit={handleTareaDetalleSubmit}>

                <label style={S.mLabel}>Título *</label>
                <input style={S.mInput} type="text"
                  value={tareaDetalleForm.titulo || ""}
                  onChange={(e) => setTareaDetalleForm((p) => ({ ...p, titulo: e.target.value }))}
                  required />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={S.mLabel}>Tipo</label>
                    <select style={S.mSelect}
                      value={tareaDetalleForm.tipo || "otro"}
                      onChange={(e) => setTareaDetalleForm((p) => ({ ...p, tipo: e.target.value }))}>
                      <option value="llamada">📞 Llamada</option>
                      <option value="visita">🏢 Visita</option>
                      <option value="enviar_cotizacion">📄 Enviar cotización</option>
                      <option value="seguimiento">🔄 Seguimiento</option>
                      <option value="administrativo">📋 Administrativo</option>
                      <option value="otro">📌 Otro</option>
                    </select>
                  </div>
                  <div>
                    <label style={S.mLabel}>Prioridad</label>
                    <select style={S.mSelect}
                      value={tareaDetalleForm.prioridad || "media"}
                      onChange={(e) => setTareaDetalleForm((p) => ({ ...p, prioridad: e.target.value }))}>
                      <option value="alta">🔴 Alta</option>
                      <option value="media">🟡 Media</option>
                      <option value="baja">🟢 Baja</option>
                    </select>
                  </div>
                </div>

                <label style={S.mLabel}>Vencimiento *</label>
                <input style={S.mInput} type="datetime-local"
                  value={tareaDetalleForm.vencimiento || ""}
                  onChange={(e) => setTareaDetalleForm((p) => ({ ...p, vencimiento: e.target.value }))}
                  required />

                <label style={S.mLabel}>Descripción</label>
                <textarea style={{ ...S.mTextarea, minHeight: 60 }}
                  placeholder="Detalles opcionales…"
                  value={tareaDetalleForm.descripcion || ""}
                  onChange={(e) => setTareaDetalleForm((p) => ({ ...p, descripcion: e.target.value }))} />

                {/* Info de solo lectura */}
                {(tareaDetalle.oportunidad_nombre || tareaDetalle.usuario_nombre) && (
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16, padding: "10px 12px", background: "#f5f3ff", borderRadius: 8, fontSize: 12, color: "#555" }}>
                    {tareaDetalle.usuario_nombre && <span>👤 {tareaDetalle.usuario_nombre}</span>}
                    {tareaDetalle.oportunidad_nombre && <span>📊 {tareaDetalle.oportunidad_nombre}</span>}
                    {tareaDetalle.completada_at && <span>✓ Completada: {formatFecha(tareaDetalle.completada_at)}</span>}
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {/* Marcar como finalizada */}
                  {tareaDetalle.estado !== "hecha" && (
                    <button type="button"
                      style={{ padding: "10px 18px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                      disabled={savingTareaDetalle}
                      onClick={() => completarTarea(tareaDetalle)}
                    >
                      ✓ Marcar como finalizada
                    </button>
                  )}
                  {tareaDetalle.estado === "hecha" && (
                    <span style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>✓ Ya finalizada</span>
                  )}
                  <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                    <button type="button" style={S.mBtnGhost} onClick={() => setTareaDetalle(null)}>Cancelar</button>
                    <button type="submit" style={S.mBtn("#7c3aed")} disabled={savingTareaDetalle}>
                      {savingTareaDetalle ? "Guardando…" : "Guardar cambios"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          MODAL: Nueva Tarea
      ═══════════════════════════════════════════════════════ */}
      {showTarea && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowTarea(false)}>
          <div style={S.modal}>
            <div style={S.mHeader("#7c3aed")}>
              <span style={S.mTitle}>✅ Nueva Tarea</span>
              <button style={S.mClose} onClick={() => setShowTarea(false)}>×</button>
            </div>
            <div style={S.mBody}>
              <form onSubmit={handleTareaSubmit}>
                <label style={S.mLabel}>Título *</label>
                <input style={S.mInput} type="text" placeholder="¿Qué hay que hacer?"
                  value={tareaForm.titulo} required
                  onChange={(e) => setTareaForm((p) => ({ ...p, titulo: e.target.value }))} />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={S.mLabel}>Tipo</label>
                    <select style={S.mSelect} value={tareaForm.tipo}
                      onChange={(e) => setTareaForm((p) => ({ ...p, tipo: e.target.value }))}>
                      <option value="llamada">📞 Llamada</option>
                      <option value="visita">🏢 Visita</option>
                      <option value="enviar_cotizacion">📄 Enviar cotización</option>
                      <option value="seguimiento">🔄 Seguimiento</option>
                      <option value="administrativo">📋 Administrativo</option>
                      <option value="otro">📌 Otro</option>
                    </select>
                  </div>
                  <div>
                    <label style={S.mLabel}>Prioridad</label>
                    <select style={S.mSelect} value={tareaForm.prioridad}
                      onChange={(e) => setTareaForm((p) => ({ ...p, prioridad: e.target.value }))}>
                      <option value="alta">🔴 Alta</option>
                      <option value="media">🟡 Media</option>
                      <option value="baja">🟢 Baja</option>
                    </select>
                  </div>
                </div>

                <label style={S.mLabel}>Vencimiento *</label>
                <input style={S.mInput} type="datetime-local"
                  value={tareaForm.vencimiento} required
                  onChange={(e) => setTareaForm((p) => ({ ...p, vencimiento: e.target.value }))} />

                <label style={S.mLabel}>Descripción (opcional)</label>
                <textarea style={{ ...S.mTextarea, minHeight: 60 }}
                  placeholder="Detalles adicionales…"
                  value={tareaForm.descripcion}
                  onChange={(e) => setTareaForm((p) => ({ ...p, descripcion: e.target.value }))} />

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" style={S.mBtnGhost} onClick={() => setShowTarea(false)}>Cancelar</button>
                  <button type="submit" style={S.mBtn("#7c3aed")} disabled={tareaSaving}>
                    {tareaSaving ? "Guardando…" : "Crear tarea"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          MODAL: Nueva Interacción
      ═══════════════════════════════════════════════════════ */}
      {showInt && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowInt(false)}>
          <div style={S.modal}>
            <div style={S.mHeader("#059669")}>
              <span style={S.mTitle}>💬 Nueva Interacción</span>
              <button style={S.mClose} onClick={() => setShowInt(false)}>×</button>
            </div>
            <div style={S.mBody}>
              <form onSubmit={handleIntSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={S.mLabel}>Cliente *</label>
                    <select style={S.mSelect} value={intClienteId} onChange={(e) => { setIntClienteId(e.target.value); setIntContactoId(""); }}>
                      <option value="">Seleccioná un cliente</option>
                      {intClientes.map((c) => <option key={c.id} value={c.id}>{c.tercero}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={S.mLabel}>Contacto *</label>
                    <select style={{ ...S.mSelect, borderColor: !intContactoId && intClienteId ? "#f59e0b" : "#e0d6dc" }}
                      value={intContactoId} onChange={(e) => setIntContactoId(e.target.value)}
                      disabled={!intClienteId || intContactos.length === 0}>
                      <option value="">{!intClienteId ? "Primero seleccioná un cliente" : intContactos.length === 0 ? "Sin contactos" : "Seleccioná un contacto"}</option>
                      {intContactos.map((c) => <option key={c.id} value={c.id}>{c.nombre}{c.cargo ? ` — ${c.cargo}` : ""}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={S.mLabel}>Fecha *</label>
                    <input style={S.mInput} type="date" value={intForm.fecha}
                      onChange={(e) => setIntForm((p) => ({ ...p, fecha: e.target.value }))} required />
                  </div>
                  <div>
                    <label style={S.mLabel}>Canal</label>
                    <select style={S.mSelect} value={intForm.canal} onChange={(e) => setIntForm((p) => ({ ...p, canal: e.target.value }))}>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="mail">Mail</option>
                      <option value="llamada">Llamada</option>
                      <option value="presencial">Presencial</option>
                    </select>
                  </div>
                </div>
                <label style={S.mLabel}>Resumen *</label>
                <textarea style={S.mTextarea} placeholder="Describí brevemente la interacción…"
                  value={intForm.resumen} required onChange={(e) => setIntForm((p) => ({ ...p, resumen: e.target.value }))} />
                <label style={S.mLabel}>Próximo paso</label>
                <textarea style={{ ...S.mTextarea, minHeight: 60 }} placeholder="¿Qué sigue? (opcional)"
                  value={intForm.proximo_paso} onChange={(e) => setIntForm((p) => ({ ...p, proximo_paso: e.target.value }))} />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" style={S.mBtnGhost} onClick={() => setShowInt(false)}>Cancelar</button>
                  <button type="submit" style={S.mBtn("#059669")} disabled={intSaving}>
                    {intSaving ? "Guardando…" : "Registrar interacción"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Inicio;
